import { useSyncExternalStore, useCallback } from 'react';

interface ScrollState {
  /** スクロール方向: 'up' | 'down' | null */
  scrollDirection: 'up' | 'down' | null;
  /** ページ最上部付近にいるか */
  isAtTop: boolean;
  /** 現在のスクロール位置 */
  scrollY: number;
  /** ヘッダーが表示されるべきか */
  shouldShowHeader: boolean;
}

interface UseScrollDirectionOptions {
  /** スクロール検知を始める閾値（px） */
  threshold?: number;
}

// グローバルな状態（useSyncExternalStore用）
let globalScrollState: ScrollState = {
  scrollDirection: null,
  isAtTop: true,
  scrollY: 0,
  shouldShowHeader: true,
};

const listeners: Set<() => void> = new Set();

function notifyListeners() {
  listeners.forEach((listener) => listener());
}

function createScrollHandler(threshold: number) {
  return function updateScrollState() {
    if (typeof window === 'undefined') return;

    const currentScrollY = window.scrollY;
    const isAtTop = currentScrollY < 50;
    const previousScrollY = globalScrollState.scrollY;

    let scrollDirection = globalScrollState.scrollDirection;
    let shouldShowHeader = globalScrollState.shouldShowHeader;

    // 閾値を超えた場合のみ方向を更新
    if (Math.abs(currentScrollY - previousScrollY) >= threshold) {
      scrollDirection = currentScrollY > previousScrollY ? 'down' : 'up';

      // ページ最上部付近では常に表示
      if (isAtTop) {
        shouldShowHeader = true;
      } else if (scrollDirection === 'down') {
        shouldShowHeader = false;
      } else if (scrollDirection === 'up') {
        shouldShowHeader = true;
      }
    }

    // 最上部に戻った場合は常に表示
    if (isAtTop && !globalScrollState.isAtTop) {
      shouldShowHeader = true;
    }

    globalScrollState = {
      scrollDirection,
      isAtTop,
      scrollY: currentScrollY,
      shouldShowHeader,
    };

    notifyListeners();
  };
}

export function useScrollDirection(
  options: UseScrollDirectionOptions = {}
): ScrollState {
  const { threshold = 10 } = options;

  const subscribe = useCallback(
    (callback: () => void) => {
      listeners.add(callback);

      const handleScroll = createScrollHandler(threshold);

      // 初回実行
      if (typeof window !== 'undefined') {
        const currentScrollY = window.scrollY;
        globalScrollState = {
          ...globalScrollState,
          scrollY: currentScrollY,
          isAtTop: currentScrollY < 50,
          shouldShowHeader: currentScrollY < 50 || globalScrollState.shouldShowHeader,
        };
      }

      window.addEventListener('scroll', handleScroll, { passive: true });

      return () => {
        listeners.delete(callback);
        window.removeEventListener('scroll', handleScroll);
      };
    },
    [threshold]
  );

  const getSnapshot = useCallback(() => {
    return globalScrollState;
  }, []);

  const getServerSnapshot = useCallback((): ScrollState => {
    return {
      scrollDirection: null,
      isAtTop: true,
      scrollY: 0,
      shouldShowHeader: true,
    };
  }, []);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
